import Link from "next/link";
import Image from "next/image";
import { VFC, memo } from "react";

import { useComments } from "hooks/articles/useComments";
import { useCommentLike } from "hooks/articles/useCommentLike";

import { CurrentUser, Comment } from "lib/type";

import { FaThumbsUp, FaRegThumbsUp } from "react-icons/fa";
import { MdAnnouncement } from "react-icons/md";
import { IoSend } from "react-icons/io5";

type CommentProps = {
  author: CurrentUser;
  user: CurrentUser;
  articleID: string;
  comment: Comment;
  deleteComment: (commentID: string, commnetUid: string) => void;
};

const CommentContent: VFC<CommentProps> = memo(
  ({ author, user, articleID, comment, deleteComment }) => {
    const { commentID, body, likeCount, createdAt, commentUser } = comment;
    const { isCurrentLiked, currentLikeCount, changeLike } = useCommentLike(
      author,
      user,
      articleID,
      commentID,
      likeCount
    );

    return (
      <li key={commentID} className="border-light-800 border-t-2 border-dotted">
        <div
          className="border-l-[0.5rem] border-solid p-4"
          style={{ borderColor: commentUser?.color }}
        >
          <p className="mb-6">{body}</p>
          <Link href={`/users/${commentUser?.uid}`} prefetch={false}>
            <a className="mr-2 mb-2 flex w-[fit-content] items-center">
              <div className="nextimg mr-1 block h-8 w-8">
                <Image
                  src={commentUser?.avatar || ""}
                  alt={commentUser?.name || ""}
                  width="50"
                  height="50"
                  className="rounded-full object-cover"
                />
              </div>
              <p>{commentUser?.name}</p>
            </a>
          </Link>
          <ul className="flex items-center">
            <li>
              <button onClick={changeLike}>
                {isCurrentLiked ? <FaThumbsUp /> : <FaRegThumbsUp />}
                <span className="ml-1">
                  {currentLikeCount < 0 ? 0 : currentLikeCount}
                </span>
              </button>
            </li>
            <li className="ml-2">
              <p>{createdAt}</p>
            </li>
            {user && commentUser && user.uid === commentUser.uid && (
              <li className="ml-auto text-sm opacity-70">
                <button
                  onClick={() => deleteComment(commentID, commentUser.uid)}
                >
                  削除
                </button>
              </li>
            )}
          </ul>
        </div>
      </li>
    );
  }
);

CommentContent.displayName = "CommentContent";

type CommentsProps = {
  user: CurrentUser;
  author: CurrentUser;
  articleID: string;
  articleTitle: string;
  signin: () => void;
};

const Comments: VFC<CommentsProps> = ({
  user,
  author,
  articleID,
  articleTitle,
  signin,
}) => {
  const {
    loadedComment,
    comments,
    commentCount,
    changeComment,
    postComment,
    deleteComment,
  } = useComments(author, user, articleID, articleTitle);

  const commentsList = comments.map((comment) => (
    <CommentContent
      key={comment.commentID}
      {...{ author, user, articleID, comment, deleteComment }}
    />
  ));

  return (
    <>
      {loadedComment && (
        <>
          <div className="px-4 md:px-0">
            <dl className="mb-2 flex items-center">
              <dt className="mr-1 flex items-center">
                <MdAnnouncement />
                コメント数
              </dt>
              <dd>{commentCount < 0 ? 0 : commentCount}</dd>
            </dl>
            <textarea
              onChange={changeComment}
              className={`mb-2 ${user ? "" : "cursor-not-allowed"}`}
              disabled={user ? false : true}
              aria-label="コメントを入力するフォーム"
            ></textarea>
            {!user && (
              <button
                onClick={signin}
                className="mb-6 inline-flex items-center"
              >
                <IoSend className="mr-1" />
                サインインしてコメントを投稿する
              </button>
            )}
            {user && (
              <button
                onClick={postComment}
                className="mb-6 inline-flex items-center"
              >
                <IoSend className="mr-1" />
                コメントを投稿する
              </button>
            )}
          </div>
          <ul>{commentsList}</ul>
        </>
      )}
    </>
  );
};

export default Comments;
